import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import Fuse from "fuse.js";
import { chain, debounce, filter, find, findIndex, isEmpty, keys, map, some } from "lodash";
import React, { startTransition, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getConfigUI } from "resbutler-ui/init";
import { MoneyUtils } from "resbutler-utils/utils/MoneyUtils";
import { Group, getProductGroupIdsFromGroups, productGroupsToGroups } from "resbutler-utils/utils/productUtils";
import { ProductCategoryId } from "../../../resbutler-utils/types/ProductCategoryId";
import { Product, ProductGroup, ProductSearchResult, ProductSize, ProductTypeId } from "../../../resbutler-utils/types/product";
import { getImagesFromProduct } from "../../../resbutler-utils/utils/menuUtils";
import { Button, Table } from "../WizElements/Elements";

interface HitsProp<T> {
  selected: ProductSearchResult[];
  setSelected: any;
  hits: Product[];
  sizes: ProductSize[];
  restaurantId: string;
  limitToOne: boolean;
  limitToOneSize: boolean;
  clientId: string;
  publicStorageBucket: string;
}

const Hits = React.memo(({ selected, setSelected, hits, sizes, restaurantId, limitToOne, limitToOneSize, clientId, publicStorageBucket }: HitsProp<Product>) => {
  const handleSelectAll = (selectAll: boolean) => {
    const newSelections = hits
      .map((hit) => {
        const selectedSizes: Record<string, boolean> = {};
        const uneditableSizes: Record<string, boolean> = {};
        const isfixedPriceMenuItem = hit.typeId === ProductTypeId.fixedPriceMenu;

        if (!isEmpty(hit.sizes) && !isfixedPriceMenuItem) {
          for (const [sizeId, isEnabled] of Object.entries(hit.sizes)) {
            const priceEnabled = isEnabled && hit?.restaurants?.[restaurantId]?.price !== undefined && hit.restaurants[restaurantId].enabled === true;

            const selectedIndex = findIndex(selected, (s) => s.uneditableSizes?.[sizeId] && s.productId === hit.id);

            if (selectedIndex === -1) {
              selectedSizes[sizeId] = priceEnabled ? selectAll : false;
            } else {
              selectedSizes[sizeId] = priceEnabled;
              uneditableSizes[sizeId] = selected[selectedIndex].uneditableSizes[sizeId];
            }
          }
        }

        return {
          productId: hit.id,
          uneditableSizes,
          sizes: selectedSizes,
        };
      })
      .filter((item) => selectAll || !isEmpty(item.sizes));

    const result = [...selected.filter((s) => !hits.some((h) => h.id === s.productId)), ...newSelections];

    setSelected(result);
  };

  return (
    <div className="mt-2">
      <Button className="my-2" type="button" color="primary" onClick={() => handleSelectAll(true)}>
        Select All
      </Button>
      <Button className="my-2 mx-3 btn btn-block border" onClick={() => handleSelectAll(false)}>
        De-select all
      </Button>
      <Table bordered>
        <tbody>
          {map(hits, (hit) => {
            const index = findIndex(selected, (s) => s.productId === hit.id);
            const { thumbnail } = getImagesFromProduct(hit, publicStorageBucket, clientId);

            return (
              <tr key={hit.id}>
                <td className="ps-3" colSpan={hit.image ? 1 : 2} style={{ borderBottom: "1px solid #ddd" }} width={"80%"}>
                  <div className="mb-1">{hit.name}</div>
                  {(hit.typeId === ProductTypeId.food || hit.typeId === ProductTypeId.beverage || hit.typeId === ProductTypeId.combinationFoodAndBeverage || hit.typeId === ProductTypeId.fixedPrice || hit.productCategoryId === ProductCategoryId.Inventory) && hit.sizes && (
                    <ul style={{ listStyleType: "none" }} className="p-0">
                      {keys(hit.sizes).map((sizeId) => {
                        if (hit.sizes[sizeId]) {
                          const priceEnabled = hit?.restaurants?.[restaurantId]?.price?.[sizeId] !== undefined && hit.restaurants[restaurantId].enabled === true;
                          const disabled = hit.productCategoryId !== ProductCategoryId.Inventory && (!priceEnabled || findIndex(selected, (s: any) => s.uneditableSizes?.[sizeId] && s.productId === hit.id) > -1);
                          const productSize = find(sizes, (p) => p.id === sizeId);
                          return (
                            <li key={`${hit.id}-${sizeId}`}>
                              <div className="checkbox c-checkbox">
                                <label className="mb-2" htmlFor={`${hit.id}-${sizeId}`}>
                                  <input
                                    id={`${hit.id}-${sizeId}`}
                                    type="checkbox"
                                    disabled={disabled}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        if (limitToOne) {
                                          setSelected([
                                            {
                                              productId: hit.id,
                                              sizes: { [sizeId]: true },
                                            },
                                          ]);
                                        } else {
                                          if (index === -1)
                                            setSelected(
                                              selected.concat({
                                                productId: hit.id,
                                                sizes: {
                                                  ...(limitToOneSize ? {} : selected[index]?.sizes),
                                                  [sizeId]: true,
                                                },
                                              })
                                            );
                                          else
                                            setSelected([
                                              ...selected.slice(0, index),
                                              {
                                                ...selected[index],
                                                sizes: {
                                                  ...(limitToOneSize ? {} : selected[index]?.sizes),
                                                  [sizeId]: true,
                                                },
                                              },
                                              ...selected.slice(index + 1),
                                            ]);
                                        }
                                      } else {
                                        setSelected([
                                          ...selected.slice(0, index),
                                          {
                                            ...selected[index],
                                            sizes: {
                                              ...selected[index]?.sizes,
                                              [sizeId]: false,
                                            },
                                          },
                                          ...selected.slice(index + 1),
                                        ]);
                                      }
                                    }}
                                    checked={findIndex(selected, (s) => s.sizes?.[sizeId] && s.productId === hit.id) > -1}
                                  />
                                  {productSize?.name} {priceEnabled ? `(${MoneyUtils.format(hit.restaurants[restaurantId].price[sizeId])})` : ""}
                                </label>
                              </div>
                            </li>
                          );
                        }
                        return null;
                      })}
                    </ul>
                  )}
                  {hit.typeId === ProductTypeId.fixedPriceMenu && (
                    <ul style={{ listStyleType: "none" }}>
                      <li>
                        <div className="checkbox c-checkbox">
                          <label className="mb-2 pe-2" htmlFor={hit.id}>
                            <input
                              id={hit.id}
                              type="checkbox"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  if (!limitToOne) setSelected([...selected.slice(0, index), { productId: hit.id }, ...selected.slice(index + 1)]);
                                  else setSelected([{ productId: hit.id }]);
                                } else {
                                  setSelected(filter(selected, (s) => s.productId !== hit.id));
                                }
                              }}
                              checked={findIndex(selected, (s) => s.productId === hit.id) > -1 ? true : false}
                            />
                            {hit.fixedPriceMenuTypes.adult?.charAt(0).toUpperCase() + hit.fixedPriceMenuTypes.adult?.slice(1)}
                          </label>
                        </div>
                      </li>
                    </ul>
                  )}
                  {(Number(hit.typeId) === ProductTypeId.extendDuration ||
                    Number(hit.typeId) === ProductTypeId.financial ||
                    Number(hit.typeId) === ProductTypeId.depositMinSpend ||
                    Number(hit.typeId) === ProductTypeId.customerServiceExpense ||
                    Number(hit.typeId) === ProductTypeId.giftCertificates) && (
                    <ul style={{ listStyleType: "none" }}>
                      <li>
                        <div className="checkbox c-checkbox">
                          <label className="mb-2 pe-2" htmlFor={hit.id}>
                            <input
                              id={hit.id}
                              type="checkbox"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  if (!limitToOne) {
                                    if (index >= 0) {
                                      setSelected([...selected.slice(0, index), { productId: hit.id }, ...selected.slice(index + 1)]);
                                    } else {
                                      setSelected([...selected, { productId: hit.id }]);
                                    }
                                  } else {
                                    setSelected([{ productId: hit.id }]);
                                  }
                                } else {
                                  setSelected(filter(selected, (s) => s.productId !== hit.id));
                                }
                              }}
                              checked={findIndex(selected, (s) => s.productId === hit.id) > -1 ? true : false}
                            />
                            {hit.name}
                          </label>
                        </div>
                      </li>
                    </ul>
                  )}
                </td>
                {hit.image ? (
                  <td className="d-flex justify-content-end" style={{ borderBottom: "1px solid #ddd" }}>
                    <div className="search-img">
                      <img src={thumbnail} style={{ maxWidth: 250 }} />
                    </div>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
});

export interface ProductSearchProps {
  sizes?: ProductSize[];
  onChange?: any;
  limitToOne?: boolean;
  restaurantId?: string;
  data?: ProductSearchResult[];
  products: Product[];
  productGroups: ProductGroup[];
  limitToOneSize?: boolean;
}

export const ProductSearch = React.memo(({ sizes, products, productGroups, onChange, limitToOne, restaurantId, data, limitToOneSize }: ProductSearchProps) => {
  const [search, setSearch] = useState("");
  const { client: clientId, config } = getConfigUI();
  const [selectedProductGroupId, setSelectedProductGroupId] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const fuse = React.useMemo(() => {
    // Merge into one list, marking type so you can tell them apart in results
    const searchList = [
      ...chain(products)
        .map((p) => ({ ...p, type: "product" }))
        .filter((p) => p.restaurants?.[restaurantId]?.enabled)
        .value(),
      ...chain(productGroups)
        .map((g) => ({ ...g, type: "productGroup" }))
        .value(),
    ];
    const options = {
      includeScore: true,
      threshold: 0.3,
      ignoreLocation: true, // don't penalize matches later in string
      minMatchCharLength: 3, // helps with short terms
      keys: [
        { name: "name", weight: 0.8 }, // strongly prefer name
        { name: "description", weight: 0.2 },
      ],
    };
    return new Fuse(searchList, options);
  }, [products, productGroups]);

  const children = filter(productGroups, (g) => !selectedProductGroupId || g.parentGroup === selectedProductGroupId);
  const groupIds: string[] = [];
  if (selectedProductGroupId) {
    groupIds.push(selectedProductGroupId);
  }
  groupIds.push(...getProductGroupIdsFromGroups(productGroups, children));

  const runSearch = debounce((term) => {
    setSearchResults(
      fuse
        .search(term)
        .map((res) => {
          return res.item;
        })
        .filter((item) => {
          return "groupId" in item && item.type === "product" && (!groupIds.length || some(groupIds, (g) => g === item.groupId));
        }) as Product[]
    );
  }, 100);

  useEffect(() => {
    if (!search.trim()) {
      setSearchResults(
        chain(products)
          .filter((p) => !groupIds.length || some(groupIds, (g) => g === p.groupId))
          .value()
      );
      return;
    }
    runSearch(search);
    // cancel debounce on unmount
    return () => runSearch.cancel();
  }, [fuse, search, selectedProductGroupId]);

  // Recursive product group rendering
  const renderGroups = (groups: Group[], depth = 0) => {
    return (
      <ul className="ms-3 ps-0">
        {map(groups, (group) => {
          return (
            <li key={group.productGroupId} className={classNames({ "fw-bold": group.productGroupId === selectedProductGroupId })}>
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedProductGroupId(selectedProductGroupId === group.productGroupId ? "" : group.productGroupId);
                }}
              >
                {group.productGroupName} ({group.count})
              </Link>
              {group.productGroups?.length > 0 && renderGroups(group.productGroups, depth + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  const pg = find(productGroups, (p) => p.id === selectedProductGroupId);

  return (
    <div className="d-flex gap-4">
      {/* Sidebar - Product Groups */}
      <div className="w-25 me-0">
        <h4 className="mb-2">Product Groups</h4>
        {renderGroups(productGroupsToGroups(productGroups, searchResults))}
      </div>

      {/* Main Content */}
      <div className="w-75">
        {/* Search Box */}
        <div className="mb-4">
          <input type="text" value={search} onChange={(e) => startTransition(() => setSearch(e.target.value))} className="border p-2 w-100" />
          <div className="fst-italic">
            Searching in {!pg ? `"all product groups"` : `"${pg.name}"`}
            {pg ? <FontAwesomeIcon icon={faXmark} className="text-danger ms-1 mt-1" onClick={() => setSelectedProductGroupId("")} /> : null}
          </div>
        </div>
        {/* Product List */}
        <div>
          {searchResults.length > 0 ? (
            <Hits hits={searchResults} restaurantId={restaurantId} selected={data} setSelected={onChange} sizes={sizes} clientId={clientId} limitToOne={limitToOne} limitToOneSize={limitToOneSize} publicStorageBucket={config.publicStorageBucket} />
          ) : (
            <div className="text-gray-500">No products found</div>
          )}
        </div>
      </div>
    </div>
  );
});
